import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { AlertTriangle } from 'lucide-react';
import { Button, ModalLayout, Textfield } from '@aimsos/ui';

/**
 * Confirm workspace deactivate (type workspace name) or reactivate (simple confirm).
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {'deactivate'|'activate'} props.mode
 * @param {string} props.expectedConfirmName — exact string the user must type to deactivate
 * @param {() => void} props.onClose
 * @param {() => void | Promise<void>} props.onConfirm
 * @param {boolean} props.submitting
 */
const WorkspaceStatusConfirmModal = ({
  isOpen,
  mode,
  expectedConfirmName = '',
  onClose,
  onConfirm,
  submitting,
}) => {
  const { t } = useTranslation('workspaces');
  const titleId = useId();
  const inputId = useId();
  const isDeactivate = mode === 'deactivate';

  const [nameInput, setNameInput] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const expected = expectedConfirmName.trim();
  const trimmedInput = nameInput.trim();

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = requestAnimationFrame(() => {
      setNameInput('');
      setSubmitted(false);
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, mode]);

  const requiredError = isDeactivate && submitted && !trimmedInput;
  const mismatchError = isDeactivate && submitted && trimmedInput && trimmedInput !== expected;

  const canSubmitDeactivate = isDeactivate && trimmedInput === expected;
  const deactivatePrimaryDisabled = submitting || !canSubmitDeactivate;

  const handleDismiss = useCallback(() => {
    if (submitting) return;
    onClose?.();
  }, [onClose, submitting]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, handleDismiss]);

  const handlePrimary = useCallback(async () => {
    if (isDeactivate) {
      setSubmitted(true);
      if (!trimmedInput || trimmedInput !== expected) return;
    }
    await onConfirm?.();
  }, [isDeactivate, trimmedInput, expected, onConfirm]);

  const title = isDeactivate
    ? t('detail.workspaceStatusModal.deactivate.title')
    : t('detail.workspaceStatusModal.activate.title');

  const closeAria = t('detail.workspaceStatusModal.closeAria');

  const supportingText = requiredError
    ? t('detail.workspaceStatusModal.deactivate.nameRequired')
    : mismatchError
      ? t('detail.workspaceStatusModal.deactivate.nameMismatch')
      : '';

  return (
    <ModalLayout
      isOpen={isOpen}
      onClose={handleDismiss}
      title={title}
      titleId={titleId}
      closeAriaLabel={closeAria}
      maxWidthClassName="max-w-lg"
      heightClassName="min-h-0 h-auto max-h-[min(90vh,40rem)]"
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
          <div className="mx-auto flex max-w-md flex-col items-center gap-5 text-center">
            <div
              className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-amber-500/15 text-amber-600 dark:bg-amber-500/20 dark:text-amber-400"
              aria-hidden
            >
              <AlertTriangle className="h-8 w-8" strokeWidth={2} />
            </div>

            {isDeactivate ? (
              <>
                <p className="max-w-md text-sm leading-relaxed text-content">
                  {t('detail.workspaceStatusModal.deactivate.description')}
                </p>
                <div className="w-full max-w-md text-left">
                  <Textfield
                    id={inputId}
                    label={t('detail.workspaceStatusModal.deactivate.inputLabel')}
                    placeholder={t('detail.workspaceStatusModal.deactivate.placeholder')}
                    value={nameInput}
                    onChange={setNameInput}
                    state={requiredError || mismatchError ? 'error' : 'default'}
                    supportingText={supportingText}
                    disabled={submitting}
                    autoComplete="off"
                  />
                </div>
              </>
            ) : (
              <p className="max-w-md text-sm leading-relaxed text-content">
                {t('detail.workspaceStatusModal.activate.description')}
              </p>
            )}
          </div>
        </div>

        <div className="shrink-0 bg-surface-muted/20 px-5 py-4">
          <div className="flex justify-center gap-4">
            <Button variant="secondary" type="button" size="md" onClick={handleDismiss} disabled={submitting}>
              {t('detail.workspaceStatusModal.cancel')}
            </Button>
            {isDeactivate ? (
              <Button
                variant="danger"
                type="button"
                size="md"
                loading={submitting}
                disabled={deactivatePrimaryDisabled}
                onClick={handlePrimary}
              >
                {t('detail.workspaceStatusModal.deactivate.confirm')}
              </Button>
            ) : (
              <Button variant="primary" type="button" size="md" loading={submitting} onClick={handlePrimary}>
                {t('detail.workspaceStatusModal.activate.confirm')}
              </Button>
            )}
          </div>
        </div>
      </div>
    </ModalLayout>
  );
};

export default WorkspaceStatusConfirmModal;
